<template>
  <v-card>
    <v-toolbar dark color="primary">
      <v-btn icon dark @click.stop="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ $t('components.PlanImporter.importPlansFor') }} {{ client.name }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn dark text @click.stop="importJSON">{{ $t('components.PlanImporter.import') }}</v-btn>
      </v-toolbar-items>
    </v-toolbar>
    <v-row>
      <v-col cols="12">
        <v-textarea v-model="json" outlined :label="$t('components.PlanImporter.json')" required rows="30" class="pl-10 pt-10 pr-10" />
        <span v-if="error.length > 0 && created.length > 0" class="ml-10">{{ $t('components.PlanImporter.createdPlans') }} {{ created }}</span>
        <span v-if="error.length > 0" class="error ml-10">{{ $t('components.PlanImporter.errors') }} {{ error }}</span>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
export default {
  data () {
    return {
      json: null,
      error: [],
      created: []
    }
  },
  methods: {
    ...mapActions('simulations', [
      'addSimulation'
    ]),
    close: function () {
      this.$emit('close-dialog')
    },
    importJSON: function () {
      this.error = []
      this.created = []
      const json = this.validJson()
      if (json && json.plans) {
        // Validate every plan's media against the target model BEFORE importing
        // anything. If any plan's channels don't match, abort the whole batch so
        // we never import a plan built for a different channel structure.
        let mismatches = []
        for (const index in json.plans) {
          const plan = json.plans[index]
          const mediaError = this.planMediaError(plan)
          if (mediaError) {
            mismatches.push(`Plan: ${plan.name || this.$t('components.PlanImporter.missingPlanName')}. ${mediaError}`)
          }
        }
        if (mismatches.length > 0) {
          this.error = mismatches
          return
        }
        let processes = []
        for (const index in json.plans) {
          const plan = json.plans[index]
          processes.push(this.createSimulation(plan).then(plan => {
            this.created.push(plan)
          }))
        }
        Promise.all(processes).then(() => {
          if (this.error.length === 0) {
            this.close()
          }
        })
      } else {
        if (!json) {
          this.error.push(this.$t('components.PlanImporter.invalidJson'))
        } else {
          this.error.push(this.$t('components.PlanImporter.noPlansToImport'))
        }
      }
    },
    planMediaError: function (sim) {
      var modelChannels = this.modelChannelKeys
      // Calculator plan: media_plan keys must match the model's channels exactly.
      if (sim.media_plan) {
        var planChannels = Object.keys(sim.media_plan)
        var missing = modelChannels.filter(function (c) { return planChannels.indexOf(c) === -1 })
        var extra = planChannels.filter(function (c) { return modelChannels.indexOf(c) === -1 })
        if (missing.length > 0 || extra.length > 0) {
          return this.$t('components.PlanImporter.channelMismatch', {
            missing: missing.length ? missing.join(', ') : '—',
            extra: extra.length ? extra.join(', ') : '—'
          })
        }
        return null
      }
      // Optimizer plan: reject only references to media (touchpoints / restriction
      // channels) that don't exist in the model. Off touchpoints are omitted from
      // the saved plan, so we don't require full coverage of the model's channels.
      var modelTps = this.modelTouchpointIds
      var bad = []
      if (Array.isArray(sim.touchpoints)) {
        sim.touchpoints.forEach(function (tp) {
          if (tp && tp.id && tp.id.indexOf('@div@') !== -1 && !modelTps[tp.id]) bad.push(tp.id)
        })
      }
      if (sim.restrictions) {
        Object.keys(sim.restrictions).forEach(function (c) {
          if (modelChannels.indexOf(c) === -1) bad.push(c)
        })
      }
      if (Array.isArray(sim.time_restrictions)) {
        sim.time_restrictions.forEach(function (r) {
          if (r && r.channelId && r.channelId !== '__all__' && modelChannels.indexOf(r.channelId) === -1) bad.push(r.channelId)
        })
      }
      if (bad.length > 0) {
        return this.$t('components.PlanImporter.touchpointMismatch', { items: bad.join(', ') })
      }
      return null
    },
    validJson: function () {
      try {
        const json = JSON.parse(this.json)
        if (json && !!this.json) {
          return json
        } else {
          return false
        }
      } catch (e) {
        return false
      }
    },
    createSimulation: function (sim) {
      return new Promise((resolve, reject) => {
        let simulation = {
          name: sim.name,
          date_from: sim.date_from,
          date_to: sim.date_to,
          budget: Number(sim.budget)
        }
        if (sim.restrictions) {
          simulation.restrictions = sim.restrictions
        }
        if (sim.touchpoints) {
          simulation.touchpoints = sim.touchpoints
        }
        if (sim.segments) {
          simulation.segments = sim.segments
        }
        if (sim.external_series_changes) {
          simulation.external_series_changes = sim.external_series_changes
        }
        if (sim.media_plan) {
          simulation.media_plan = sim.media_plan
        }
        if (sim.type) {
          simulation.type = sim.type
        }
        if (sim.media_plan_frequency) {
          simulation.media_plan_frequency = sim.media_plan_frequency
        }
        if (sim.external_series_frequency) {
          simulation.external_series_frequency = sim.external_series_frequency
        }
        // Beta optimizer (optimizer_version === 2) fields. Use !== undefined so
        // meaningful falsy values are preserved: enable_time can legitimately be
        // false (a v2 plan with budget-mode restrictions), and optimizer_version
        // is numeric. Legacy v1 JSON lacks these keys, so every check is skipped.
        if (sim.optimizer_version !== undefined) {
          simulation.optimizer_version = sim.optimizer_version
        }
        if (sim.budget_allocation !== undefined) {
          simulation.budget_allocation = sim.budget_allocation
        }
        if (sim.kpi_weighting !== undefined) {
          simulation.kpi_weighting = sim.kpi_weighting
        }
        if (sim.enable_time !== undefined) {
          simulation.enable_time = sim.enable_time
        }
        if (sim.time_restrictions !== undefined) {
          simulation.time_restrictions = sim.time_restrictions
        }
        this.addSimulation({
          client: this.client.id,
          user: this.createdBy,
          userId: this.createdById,
          baseSimulation: 'JSON imported',
          simulation: simulation
        }).then(() => {
          resolve(simulation.name)
        }).catch(error => {
          this.error.push(`Plan: ${simulation.name || this.$t('components.PlanImporter.missingPlanName')}. Message: ${error}`)
          reject(error)
        })
      })
    }
  },
  computed: {
    referenceModelConfig: function () {
      if (this.client.plan_reference_model) return this.client.plan_reference_model.config
      if (this.client.reference_model) return this.client.reference_model.config
      return null
    },
    modelChannelKeys: function () {
      if (!this.referenceModelConfig || !this.referenceModelConfig.media) return []
      return Object.keys(this.referenceModelConfig.media)
    },
    modelTouchpointIds: function () {
      var ids = {}
      if (this.referenceModelConfig && this.referenceModelConfig.media) {
        var media = this.referenceModelConfig.media
        Object.keys(media).forEach(function (channel) {
          (media[channel] || []).forEach(function (tp) { ids[channel + '@div@' + tp] = true })
        })
      }
      return ids
    }
  },
  props: {
    client: {
      type: Object,
      required: true
    },
    createdBy: {
      type: String,
      required: true
    },
    createdById: {
      type: String,
      required: true
    }
  }
}
</script>
